import { LitElement, html, css } from 'lit';
import { customElement } from 'lit/decorators.js';
import { customerBrandStyles } from '../../styles/customer-brand-styles';
import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';
import { setUserContextMode } from '../../state/user-context-mode';
import { resolveRouterPath, router } from '../../router';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';

@customElement('app-mode-picker')
export class AppModePicker extends WithUserProfileConsumer(LitElement) {
  static styles = [
    customerBrandStyles,
    css`
      :host {
        display: block;
      }

      .page {
        align-items: center;
        justify-content: flex-start;
        padding: clamp(1.5rem, 6vw, 3rem) 0 3rem;
      }

      /* Single subtle blob — strategic atmosphere, not decoration. */
      .blob.a {
        top: -260px;
        right: -200px;
        width: 460px;
        height: 460px;
        background: var(--coral);
        opacity: 0.18;
      }

      .stage {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: 760px;
        padding: 0 clamp(1.25rem, 5vw, 2rem);
        box-sizing: border-box;
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      /* Brand block (prominent at top) */
      .brand-block {
        display: flex;
        align-items: center;
        gap: 14px;
        margin: 0 auto 3rem;
      }
      .brand-block img {
        width: clamp(44px, 12vw, 56px);
        height: clamp(44px, 12vw, 56px);
        border-radius: 14px;
      }
      .brand-block .wordmark {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: clamp(22px, 6.5vw, 30px);
        letter-spacing: -0.03em;
        color: var(--brand-navy);
        line-height: 1;
      }
      .brand-block .wordmark span {
        color: var(--brand-gold);
      }

      /* Greeting */
      .greeting {
        text-align: center;
        margin-bottom: 2.5rem;
      }
      .greeting h1 {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: clamp(1.9rem, 6.5vw, 2.8rem);
        line-height: 1.05;
        letter-spacing: -0.03em;
        margin: 0 0 0.55rem;
        color: var(--ink);
      }
      .greeting h1 .wave {
        display: inline-block;
        font-weight: 400;
        margin-left: 0.15em;
        transform-origin: 70% 70%;
        animation: wave 2.6s ease-in-out infinite;
      }
      .greeting p {
        margin: 0;
        font-size: 1.05rem;
        font-weight: 500;
        color: var(--ink-soft);
      }

      /* Two cards: vibrant + soft */
      .cards {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1rem;
        margin-bottom: 2rem;
      }
      @media (max-width: 560px) {
        .cards {
          grid-template-columns: 1fr;
        }
      }

      .card {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding: 1.85rem 1.6rem;
        border-radius: 22px;
        cursor: pointer;
        text-align: left;
        text-decoration: none;
        color: inherit;
        font: inherit;
        background: var(--canvas);
        border: 1px solid var(--line);
        box-shadow: var(--shadow-soft);
        transition:
          transform 220ms cubic-bezier(0.16, 0.84, 0.44, 1),
          box-shadow 220ms ease,
          border-color 220ms ease;
        overflow: hidden;
        min-height: 220px;
      }
      .card:hover {
        transform: translateY(-3px);
        box-shadow: var(--shadow-lift);
      }
      .card.customer {
        background: var(--tint-sun);
      }
      .card.customer:hover {
        border-color: var(--sun-deep);
      }
      .card.shop {
        background: var(--tint-sky);
      }
      .card.shop:hover {
        border-color: var(--brand-navy);
      }

      .icon-circle {
        width: 56px;
        height: 56px;
        border-radius: 18px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        color: white;
        flex: none;
      }
      /* Customer keeps the rose heart (love mark stays meaningful) */
      .card.customer .icon-circle {
        background: var(--coral);
        box-shadow: 0 6px 14px var(--coral-glow);
      }
      /* Shop icon uses navy — previews the operator theme */
      .card.shop .icon-circle {
        background: var(--brand-navy);
        color: white;
        box-shadow: 0 6px 14px rgba(15, 42, 68, 0.25);
      }

      .card-title {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: 1.45rem;
        line-height: 1.1;
        letter-spacing: -0.025em;
        color: var(--ink);
      }
      .card-desc {
        font-size: 0.9rem;
        font-weight: 500;
        color: var(--ink-soft);
        line-height: 1.45;
        flex: 1;
      }
      .card-cta {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-weight: 700;
        font-size: 0.92rem;
        color: var(--ink);
        margin-top: 4px;
      }
      .card.customer .card-cta {
        color: var(--sun-deep);
      }
      .card.shop .card-cta {
        color: var(--brand-navy);
      }
      .card-cta sl-icon {
        font-size: 14px;
        transition: transform 200ms ease;
      }
      .card:hover .card-cta sl-icon {
        transform: translateX(4px);
      }

      .foot {
        text-align: center;
        font-size: 0.82rem;
        color: var(--ink-faint);
        font-weight: 500;
      }
    `,
  ];

  private _pick(mode: 'customer' | 'shop') {
    setUserContextMode(mode);
    if (mode === 'customer') {
      router.navigate(resolveRouterPath('customer'));
    } else {
      router.navigate(resolveRouterPath('dashboard'));
    }
  }

  render() {
    const name = this.userProfile?.displayName?.split(' ')[0] ?? '';
    return html`
      <div class="page">
        <div class="blob a"></div>
        <div class="stage">
          <div class="brand-block pa-rise">
            <img src="/assets/icons/pag-asenso-icon.svg" alt="" />
            <span class="wordmark">Pag<span>-asenso</span></span>
          </div>

          <div class="greeting pa-rise pa-rise-2">
            <h1>
              ${name ? `Hello, ${name}` : 'Hello'}<span class="wave">👋</span>
            </h1>
            <p>What brings you here today?</p>
          </div>

          <div class="cards pa-rise pa-rise-3">
            <button
              class="card customer"
              @click=${() => this._pick('customer')}
            >
              <span class="icon-circle"
                ><sl-icon name="heart-fill"></sl-icon
              ></span>
              <div class="card-title">I'm shopping</div>
              <div class="card-desc">
                Your loyalty rewards and the shops you love — all in one place.
              </div>
              <span class="card-cta"
                >Open customer view <sl-icon name="arrow-right"></sl-icon
              ></span>
            </button>

            <button class="card shop" @click=${() => this._pick('shop')}>
              <span class="icon-circle"><sl-icon name="shop"></sl-icon></span>
              <div class="card-title">I'm running a shop</div>
              <div class="card-desc">
                Take orders, manage stock, and grow your business.
              </div>
              <span class="card-cta"
                >Open my shop <sl-icon name="arrow-right"></sl-icon
              ></span>
            </button>
          </div>

          <div class="foot pa-rise pa-rise-4">
            Switch any time from the menu.
          </div>
        </div>
      </div>
    `;
  }
}
